import {
  createContext,
  ReactNode,
  useState,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import {
  SharedBasketItemCategory,
  SharedBasketItemInput,
} from '@/lib/types';

interface BasketContextType {
  items: SharedBasketItemInput[];
  count: number;
  has: (
    category: SharedBasketItemCategory,
    fileId: number,
  ) => boolean;
  toggle: (
    category: SharedBasketItemCategory,
    fileId: number,
  ) => void;
  addMany: (
    category: SharedBasketItemCategory,
    fileIds: number[],
  ) => void;
  removeMany: (
    category: SharedBasketItemCategory,
    fileIds: number[],
  ) => void;
  clear: () => void;
  idsFor: (category: SharedBasketItemCategory) => number[];
}

export const useBasketContext = () => {
  const context = useContext(BasketContext);
  if (!context) {
    throw new Error(
      'useBasketContext must be used within a BasketContextProvider',
    );
  }
  return context;
};

const BasketContext = createContext<BasketContextType | undefined>(
  undefined,
);

export const BasketContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [items, setItems] = useState<SharedBasketItemInput[]>([]);

  const has = useCallback(
    (category: SharedBasketItemCategory, fileId: number) =>
      items.some(
        (item) =>
          item.category === category && item.file_id === fileId,
      ),
    [items],
  );

  const toggle = useCallback(
    (category: SharedBasketItemCategory, fileId: number) => {
      setItems((prev) => {
        const exists = prev.some(
          (item) =>
            item.category === category && item.file_id === fileId,
        );
        return exists
          ? prev.filter(
              (item) =>
                !(
                  item.category === category &&
                  item.file_id === fileId
                ),
            )
          : [...prev, { category, file_id: fileId }];
      });
    },
    [],
  );

  // Idempotent on purpose: the create API rejects a duplicate
  // (category, file_id) pair with a 422, so a duplicate must be
  // unrepresentable here rather than filtered out later.
  const addMany = useCallback(
    (category: SharedBasketItemCategory, fileIds: number[]) => {
      setItems((prev) => {
        const present = new Set(
          prev
            .filter((item) => item.category === category)
            .map((item) => item.file_id),
        );
        const additions = fileIds
          .filter((fileId) => !present.has(fileId))
          .map((fileId) => ({ category, file_id: fileId }));
        return additions.length ? [...prev, ...additions] : prev;
      });
    },
    [],
  );

  const removeMany = useCallback(
    (category: SharedBasketItemCategory, fileIds: number[]) => {
      setItems((prev) => {
        const removals = new Set(fileIds);
        const next = prev.filter(
          (item) =>
            !(
              item.category === category &&
              removals.has(item.file_id)
            ),
        );
        return next.length === prev.length ? prev : next;
      });
    },
    [],
  );

  const clear = useCallback(() => setItems([]), []);

  // Adapter for the surfaces, which hand `FileExplorer` a plain
  // number[] of ids for a single category.
  const idsFor = useCallback(
    (category: SharedBasketItemCategory) =>
      items
        .filter((item) => item.category === category)
        .map((item) => item.file_id),
    [items],
  );

  const value = useMemo(
    () => ({
      items,
      count: items.length,
      has,
      toggle,
      addMany,
      removeMany,
      clear,
      idsFor,
    }),
    [items, has, toggle, addMany, removeMany, clear, idsFor],
  );

  return (
    <BasketContext.Provider value={value}>
      {children}
    </BasketContext.Provider>
  );
};

export { BasketContext };
export default BasketContextProvider;
